/**
 * `Drafter` — the seam the real drafting model (M-08) replaces.
 *
 * For M1 there is NO model in this app. This adapter maps a demo utterance onto
 * a `dcta.draft/1` fixture that a human already reviewed
 * (`packages/contract/examples/`, `evals/scenarios/drafts/`), and an utterance
 * it does not hold produces nothing — an honest "I could not draft that" card,
 * never a guess. Two consequences, both deliberate:
 *
 *  - The transcript POSTed to `/v1/drafts` is the FIXTURE's transcript, because
 *    the draft's provenance spans are anchored to those exact bytes and the
 *    resolver re-anchors them. Matching is therefore exact after normalisation,
 *    never fuzzy: the customer's words and the fixture's words differ only in
 *    case, punctuation and whether a number was spelled out.
 *  - `modelId` says what it is. It is signed into the payload's `drafter` block,
 *    so the audit trail records that an M1 demo bundle was drafted by a fixture.
 */

export interface DraftFixture {
  readonly id: string;
  readonly source: string;
  readonly transcript: string;
  readonly draft: unknown;
}

export interface DraftMatch {
  readonly id: string;
  readonly transcript: string;
  readonly draft: unknown;
}

const UNITS: Readonly<Record<string, number>> = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
  seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50,
  sixty: 60, seventy: 70, eighty: 80, ninety: 90,
};
const SCALES: Readonly<Record<string, number>> = { hundred: 100, thousand: 1000, million: 1_000_000 };

/**
 * Case, punctuation and number words folded away. This is a MATCHING key only —
 * it never reaches the gateway, and the independent number re-parser in the
 * resolver (the 50 vs 50,000 guard) is what actually reads an amount.
 */
export function normalise(utterance: string): string {
  const words = utterance
    .toLowerCase()
    .replace(/[‘’']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter((w) => w !== "");

  const out: string[] = [];
  let current = 0;
  let running = 0;
  let open = false;
  // The value of the last unit word, or `undefined` right after a scale word.
  // It decides whether the next unit CONTINUES this number ("twenty" + "five"
  // = 25) or starts a new one ("four" "five" "two" "one" = a spelled-out
  // digit string, which is how a customer reads out an account number).
  let lastUnit: number | undefined;

  const flush = (): void => {
    if (!open) return;
    out.push(String(running + current));
    current = 0;
    running = 0;
    open = false;
    lastUnit = undefined;
  };

  for (let i = 0; i < words.length; i += 1) {
    const word = words[i] as string;
    if (UNITS[word] !== undefined) {
      const value = UNITS[word] as number;
      if (open && lastUnit !== undefined && !(lastUnit >= 20 && value < 10)) flush();
      current += value;
      lastUnit = value;
      open = true;
      continue;
    }
    if (SCALES[word] !== undefined) {
      const scale = SCALES[word] as number;
      if (!open) {
        out.push(word);
        continue;
      }
      if (scale === 100) {
        current *= 100;
      } else {
        running = (running + current) * scale;
        current = 0;
      }
      lastUnit = undefined;
      continue;
    }
    // "four hundred AND twelve" — "and" only joins when a number is open and a
    // number follows, otherwise it is an ordinary word.
    if (word === "and" && open && UNITS[words[i + 1] ?? ""] !== undefined) continue;
    if (/^\d+$/.test(word)) {
      flush();
      out.push(String(Number(word)));
      continue;
    }
    flush();
    out.push(word);
  }
  flush();
  return out.join(" ");
}

export class FixtureDrafter {
  /** Signed into the payload. It must never read like a model id. */
  readonly modelId = "m1-fixture-drafter/1 (NOT a model — the M-08 stand-in)";

  readonly #byKey = new Map<string, DraftFixture>();
  readonly #all: readonly DraftFixture[];

  constructor(fixtures: readonly DraftFixture[]) {
    this.#all = fixtures;
    for (const fixture of fixtures) {
      const key = normalise(fixture.transcript);
      // First wins: the contract examples are loaded before the corner-case
      // scenarios, so a demo utterance always resolves to its reviewed example.
      if (!this.#byKey.has(key)) this.#byKey.set(key, fixture);
    }
  }

  draft(utterance: string): DraftMatch | undefined {
    const hit = this.#byKey.get(normalise(utterance));
    if (hit === undefined) return undefined;
    return { id: hit.id, transcript: hit.transcript, draft: hit.draft };
  }

  utterances(): readonly DraftFixture[] {
    return this.#all;
  }

  /**
   * Answering a clarification needs a drafting model that can amend a draft in
   * place; M1 has none, and there is no "answer this question" route on the
   * gateway yet (README, "Asks for `backend-architect`"). So a tapped choice
   * puts a sentence the drafter CAN handle into the composer, editable, and the
   * customer sends it — which is the designed "correct it before you send"
   * behaviour rather than a guess made on their behalf.
   *
   * The match is on the candidate's own digits: "•••• 4521" finds the fixture
   * that spells "four five two one". Nothing is inferred beyond that.
   */
  suggestFollowUp(original: string, maskedProxy: string | undefined): string | undefined {
    const digits = (maskedProxy ?? "").replace(/\D/g, "");
    if (digits.length < 3) return undefined;
    const words = new Set(normalise(original).split(" "));
    let best: DraftFixture | undefined;
    for (const fixture of this.#all) {
      const compact = normalise(fixture.transcript).replace(/(?<=\d) (?=\d)/g, "");
      if (!compact.includes(digits)) continue;
      const shared = compact.split(" ").filter((token) => words.has(token)).length;
      if (shared < 2) continue;
      if (best === undefined || fixture.transcript.length < best.transcript.length) best = fixture;
    }
    return best?.transcript;
  }
}
